/**
 * Client and site records (docs/decisions/0014): what the client list needs to know about a
 * project without opening it — the client, the installation's address, the last inspection and
 * when the next one is due — and how projects group into clients and sites.
 *
 * Due dates (RGIE/AREI, Belgium):
 * - after a non-conform inspection: the deadline on the report, else 12 months after the visit
 *   (the re-inspection must be done by the same body);
 * - after a conform one: the periodic inspection, 25 years later for a domestic installation,
 *   5 years for a non-domestic one.
 * Other standards: only the deadline written on a non-conform report.
 */
import type { Inspection, InstallationUsage, Project, Standard } from './types.ts';

export interface SiteSummary {
  projectId: string;
  title: string;
  client: { name: string; address: string; email: string; phone: string };
  /** Installation address (the owner's when the same). */
  site: string;
  standard: Standard;
  usage: InstallationUsage;
  lastInspection?: { date: string; result: Inspection['result']; body: string };
  openRemarks: number;
  due?: InspectionDue;
  /** ISO time the summary was made (when the project was last seen). */
  seen: string;
}

export interface InspectionDue {
  /** ISO date. */
  date: string;
  kind: 'reinspection' | 'periodic';
}

/** Periodic inspection interval in years (RGIE: 25 domestic, 5 non-domestic); null elsewhere. */
export const periodicYears = (standard: Standard, usage: InstallationUsage): number | null =>
  standard === 'rgie' ? (usage === 'non-domestic' ? 5 : 25) : null;

const addMonths = (iso: string, months: number): string => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1 + months, 1));
  // The same day of the month, or the last day when the month is shorter (31 Jan + 1 → 28 Feb).
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(d, last));
  return t.toISOString().slice(0, 10);
};

/** The project's latest inspection report, by date. */
export const latestInspection = (p: Project): Inspection | undefined =>
  Object.values(p.inspections ?? {}).sort(
    (a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id),
  )[0];

/** When the next inspection of the installation is due, from its latest inspection report. */
export function inspectionDue(p: Project): InspectionDue | undefined {
  const last = latestInspection(p);
  if (!last || !/^\d{4}-\d{2}-\d{2}/.test(last.date)) return undefined;
  const standard = p.meta.standard ?? 'rgie';
  if (last.result === 'not-conform') {
    if (last.deadline) return { date: last.deadline.slice(0, 10), kind: 'reinspection' };
    return standard === 'rgie' ? { date: addMonths(last.date, 12), kind: 'reinspection' } : undefined;
  }
  if (last.result !== 'conform') return undefined;
  const years = periodicYears(standard, p.meta.usage ?? 'domestic');
  return years ? { date: addMonths(last.date, 12 * years), kind: 'periodic' } : undefined;
}

export function siteSummary(p: Project, seen = new Date().toISOString()): SiteSummary {
  const m = p.meta;
  const last = latestInspection(p);
  const due = inspectionDue(p);
  return {
    projectId: p.id,
    title: m.title,
    client: {
      name: m.ownerName,
      address: m.ownerAddress,
      email: m.ownerEmail ?? '',
      phone: m.ownerPhone ?? '',
    },
    site: m.installationAddress || m.ownerAddress,
    standard: m.standard ?? 'rgie',
    usage: m.usage ?? 'domestic',
    ...(last ? { lastInspection: { date: last.date, result: last.result, body: last.body } } : {}),
    openRemarks: Object.values(p.remarks ?? {}).filter((r) => r.status === 'open').length,
    ...(due ? { due } : {}),
    seen,
  };
}

/** Name or address in a form that matches the same client typed twice ("Dupont  M." = "dupont m"). */
export const normaliseKey = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

export interface ClientRecord {
  key: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  sites: Array<{ key: string; address: string; projects: SiteSummary[]; due?: InspectionDue }>;
}

/**
 * Projects grouped by client (same name, else same address), then by installation address.
 * The newest project of a site gives its due date; clients are in name order.
 */
export function groupClients(summaries: SiteSummary[]): ClientRecord[] {
  const clients = new Map<string, ClientRecord>();
  for (const s of [...summaries].sort((a, b) => b.seen.localeCompare(a.seen))) {
    const key = normaliseKey(s.client.name) || `@${normaliseKey(s.site)}` || `#${s.projectId}`;
    const c = clients.get(key) ?? {
      key,
      // The newest project's spelling, tidied.
      name: s.client.name.trim().replace(/\s+/g, ' '),
      email: '',
      phone: '',
      address: s.client.address,
      sites: [],
    };
    c.email ||= s.client.email;
    c.phone ||= s.client.phone;
    c.address ||= s.client.address;
    const siteKey = normaliseKey(s.site) || `#${s.projectId}`;
    let site = c.sites.find((x) => x.key === siteKey);
    if (!site) c.sites.push((site = { key: siteKey, address: s.site, projects: [] }));
    site.projects.push(s);
    clients.set(key, c);
  }
  for (const c of clients.values())
    for (const site of c.sites) {
      // The latest inspection of the site decides, whichever project holds it.
      const withInspection = site.projects
        .filter((p) => p.lastInspection)
        .sort((a, b) => b.lastInspection!.date.localeCompare(a.lastInspection!.date));
      const due = withInspection[0]?.due;
      if (due) site.due = due;
    }
  const sortKey = (c: ClientRecord) => normaliseKey(c.name || c.address);
  return [...clients.values()].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
}

export interface Reminder {
  client: ClientRecord;
  site: ClientRecord['sites'][number];
  due: InspectionDue;
  /** Days from `today` (negative: overdue). */
  inDays: number;
}

/** Inspections due within `withinDays` (and every overdue one), soonest first. */
export function reminders(clients: ClientRecord[], today: string, withinDays = 180): Reminder[] {
  const t0 = Date.parse(today.slice(0, 10));
  const out: Reminder[] = [];
  for (const client of clients)
    for (const site of client.sites) {
      if (!site.due) continue;
      const inDays = Math.round((Date.parse(site.due.date) - t0) / 86_400_000);
      if (inDays <= withinDays) out.push({ client, site, due: site.due, inDays });
    }
  return out.sort((a, b) => a.inDays - b.inDays);
}

const icsText = (s: string) =>
  s
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/[,;]/g, (c) => `\\${c}`);

/**
 * Due inspections as an iCalendar file (all-day events), to import in a calendar. `label`
 * gives each event's title.
 */
export function remindersIcs(
  items: Array<{ due: InspectionDue; uid: string; title: string; description: string }>,
  now = new Date(),
): string {
  const stamp = now
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d+Z$/, 'Z');
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Tablo//Inspections//EN', 'CALSCALE:GREGORIAN'];
  for (const it of items) {
    const d = it.due.date.replace(/-/g, '');
    const next = new Date(Date.parse(it.due.date) + 86_400_000).toISOString().slice(0, 10).replace(/-/g, '');
    lines.push(
      'BEGIN:VEVENT',
      `UID:${it.uid}@tablo-app.be`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${d}`,
      `DTEND;VALUE=DATE:${next}`,
      `SUMMARY:${icsText(it.title)}`,
      `DESCRIPTION:${icsText(it.description)}`,
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${icsText(it.title)}`,
      'TRIGGER:-P30D',
      'END:VALARM',
      'END:VEVENT',
    );
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}
